import { zodResolver } from "@hookform/resolvers/zod";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import React from "react";
import { useForm } from "react-hook-form";

import { useToast } from "@/components/ui/use-toast";
import { useI18n } from "@/context/i18n";
import { formatCpfCnpj } from "@/core/utils";

import * as clientsApi from "../clients/clients.api";
import { TClientExtendedResponse } from "../clients/clients.types";
import {
  organizationRegisterFormSchema,
  TOrganizationRegisterFormData,
} from "./organization-register.schema";
import * as projectsApi from "./projects.api";

export interface IUseOrganizationRegister {
  form: ReturnType<typeof useForm<TOrganizationRegisterFormData>>;
  isSearching: boolean;
  isSubmitting: boolean;
  foundOrganization: any | null;
  handleCpfSearch: (cpf: string) => Promise<void>;
  handleSubmit: (data: TOrganizationRegisterFormData) => Promise<void>;
  resetForm: () => void;
  handleAddressBlur: (event: React.FocusEvent<HTMLInputElement>) => Promise<void>;
}

export const defaultOrganizationFormValues: TOrganizationRegisterFormData = {
  document_type: "cnpj",
  name: "",
  cnpj: "",
  cpf: "",
  corporate_name: "",
  email: "",
  business_classification: "",
  address: {
    postal_code: "",
    street: "",
    number: "",
    complement: "",
    neighborhood: "",
    city: "",
    state: "",
  },
  company_representative: {
    first_name: "",
    last_name: "",
    personal_email: "",
    cpf: "",
    mobile_phone: "",
  },
  bank_account: {
    bank_name: "",
    bank_branch: "",
    checking_account: "",
    checking_account_digit: "",
  },
  document: {
    company_logo: undefined,
    cnpj_card: undefined,
    partners_documents_cpf: [],
    articles_of_incorporation: [],
    contract_signed: [],
  },
  signed_terms: {
    signed: true,
    signed_at: new Date().toISOString(),
    ip_address: "127.0.0.1",
  },
};

function stripDocumentDigits(value?: string | null): string {
  return value?.replace(/\D/g, "") ?? "";
}

function formatDocumentField(value?: string | null): string {
  const digits = stripDocumentDigits(value);
  return digits ? formatCpfCnpj(digits) : "";
}

function businessClassificationToFormValue(bc: string | undefined): string {
  if (!bc) return "servicos";
  if (bc === "comercial") return "comercio";
  if (bc === "comercio" || bc === "industria" || bc === "servicos" || bc === "rural") {
    return bc;
  }
  return "servicos";
}

function resolveSubPartnerDocumentType(
  data: Pick<TClientExtendedResponse, "document_type" | "cpf" | "cnpj"> & {
    company_representative?: { cpf?: string };
  },
): "cpf" | "cnpj" {
  if (data.document_type === "cpf" || data.document_type === "cnpj") {
    return data.document_type;
  }

  const cpfDigits =
    stripDocumentDigits(data.cpf) ||
    stripDocumentDigits(data.company_representative?.cpf);
  const cnpjDigits = stripDocumentDigits(data.cnpj);

  if (cnpjDigits.length >= 14) return "cnpj";
  if (cpfDigits.length >= 11) return "cpf";
  if (!data.cnpj && cpfDigits) return "cpf";

  return "cnpj";
}

function mapSubPartnerDocumentFieldsFromApi(
  data: TClientExtendedResponse,
): TOrganizationRegisterFormData["document"] {
  const apiDoc = data.document;
  if (!apiDoc) {
    return defaultOrganizationFormValues.document;
  }

  const rawContracts =
    apiDoc.signed_contract ??
    (apiDoc as { contract_signed?: string | string[] | null }).contract_signed ??
    [];
  const contractUrls = (
    Array.isArray(rawContracts)
      ? rawContracts
      : typeof rawContracts === "string" && rawContracts.length > 0
        ? [rawContracts]
        : []
  ).filter((item): item is string => typeof item === "string" && item.length > 0);

  return {
    company_logo:
      typeof apiDoc.company_logo === "string" && apiDoc.company_logo.length > 0
        ? apiDoc.company_logo
        : undefined,
    cnpj_card:
      typeof apiDoc.cnpj_card === "string" && apiDoc.cnpj_card.length > 0
        ? apiDoc.cnpj_card
        : undefined,
    contract_signed: contractUrls,
  } as unknown as TOrganizationRegisterFormData["document"];
}

/** Mapeia GET /organizations/:id para o formulário de sub-parceiro (criação/edição). */
export function mapSubPartnerApiToFormValues(
  data: TClientExtendedResponse,
): TOrganizationRegisterFormData {
  const documentType = resolveSubPartnerDocumentType(data);
  // Listagem usa o CPF da org; se o GET detalhe omitir, cai no CPF do representante.
  const orgCpfDigits =
    stripDocumentDigits(data.cpf) ||
    stripDocumentDigits(data.company_representative?.cpf);
  const orgCnpjDigits = stripDocumentDigits(data.cnpj);
  const representativeCpfDigits =
    stripDocumentDigits(data.company_representative?.cpf) || orgCpfDigits;

  return {
    document_type: documentType,
    name: data.name ?? "",
    cnpj: documentType === "cnpj" ? formatDocumentField(orgCnpjDigits) : "",
    cpf: formatDocumentField(orgCpfDigits),
    corporate_name: data.corporate_name ?? "",
    email: data.email ?? "",
    business_classification: businessClassificationToFormValue(
      data.business_classification as string | undefined,
    ),
    address: {
      postal_code: data.address?.postal_code?.replace(/\D/g, "") ?? "",
      street: data.address?.street ?? "",
      number: data.address?.number ?? "",
      complement: data.address?.complement ?? "",
      neighborhood: data.address?.neighborhood ?? "",
      city: data.address?.city ?? "",
      state: data.address?.state ?? "",
    },
    company_representative: {
      first_name: data.company_representative?.first_name ?? "",
      last_name: data.company_representative?.last_name ?? "",
      personal_email: data.company_representative?.personal_email ?? "",
      cpf: formatDocumentField(representativeCpfDigits),
      mobile_phone:
        data.company_representative?.mobile_phone?.replace(/\D/g, "") ?? "",
    },
    bank_account: {
      bank_name: data.bank_account?.bank_name ?? "",
      bank_branch: data.bank_account?.bank_branch ?? "",
      checking_account: data.bank_account?.checking_account ?? "",
      checking_account_digit: data.bank_account?.checking_account_digit ?? "",
    },
    document: mapSubPartnerDocumentFieldsFromApi(data),
    signed_terms: {
      signed: true,
      signed_at: new Date().toISOString(),
      ip_address: "127.0.0.1",
    },
  };
}

export function buildOrganizationFormData(data: TOrganizationRegisterFormData): FormData {
  const formData = new FormData();

  const jsonData: Record<string, unknown> = {
    name: data.name,
    corporate_name: data.document_type === "cnpj" ? data.corporate_name : data.name,
    email: data.email,
    type_partner: "client",
    business_classification: data.business_classification,
    address: {
      postal_code: data.address.postal_code.replace(/\D/g, ""),
      street: data.address.street,
      number: data.address.number,
      complement: data.address.complement || "",
      neighborhood: data.address.neighborhood,
      city: data.address.city,
      state: data.address.state,
    },
    company_representative: {
      first_name: data.company_representative.first_name,
      last_name: data.company_representative.last_name,
      personal_email: data.company_representative.personal_email,
      cpf: data.company_representative.cpf.replace(/\D/g, ""),
      mobile_phone: data.company_representative.mobile_phone.replace(/\D/g, ""),
    },
    bank_account: {
      bank_name: data.bank_account.bank_name,
      bank_branch: data.bank_account.bank_branch,
      checking_account: data.bank_account.checking_account,
      checking_account_digit: data.bank_account.checking_account_digit,
    },
    signed_terms: data.signed_terms,
  };

  if (data.document_type === "cnpj") {
    jsonData.cnpj = (data.cnpj ?? "").replace(/\D/g, "");
  } else {
    jsonData.cpf = (data.cpf ?? "").replace(/\D/g, "");
  }

  formData.append("data", JSON.stringify(jsonData));

  if (data.document.company_logo) {
    formData.append("document.company_logo", data.document.company_logo);
  }
  if (data.document.cnpj_card) {
    formData.append("document.cnpj_card", data.document.cnpj_card);
  }
  if (data.document.partners_documents_cpf) {
    data.document.partners_documents_cpf.forEach((file) => {
      formData.append("document.partners_documents_cpf", file);
    });
  }
  if (data.document.articles_of_incorporation) {
    data.document.articles_of_incorporation.forEach((file) => {
      formData.append("document.articles_of_incorporation", file);
    });
  }
  if (data.document.contract_signed) {
    data.document.contract_signed.forEach((file) => {
      formData.append("document.contract_signed", file);
    });
  }

  return formData;
}

function toISO8601Date(value?: string | Date | null): string {
  if (value == null || value === "") {
    return new Date().toISOString().split("T")[0]!;
  }
  if (value instanceof Date) {
    return value.toISOString().split("T")[0]!;
  }
  const trimmed = value.trim();
  if (!trimmed) {
    return new Date().toISOString().split("T")[0]!;
  }
  const date = new Date(trimmed);
  if (!Number.isNaN(date.getTime())) {
    return date.toISOString().split("T")[0]!;
  }
  return new Date().toISOString().split("T")[0]!;
}

/** Payload para POST /organizations/:parentId/sub-partners (createSubPartnerDto). */
export function buildSubPartnerFormData(data: TOrganizationRegisterFormData): FormData {
  const formData = new FormData();

  const jsonData: Record<string, unknown> = {
    name: data.name,
    corporate_name: data.document_type === "cnpj" ? data.corporate_name : data.name,
    email: data.email,
    opening_date: toISO8601Date(),
    business_classification: data.business_classification,
    address: {
      postal_code: data.address.postal_code.replace(/\D/g, ""),
      street: data.address.street,
      number: data.address.number,
      complement: data.address.complement || "",
      neighborhood: data.address.neighborhood,
      city: data.address.city,
      state: data.address.state,
    },
    company_representative: {
      first_name: data.company_representative.first_name,
      last_name: data.company_representative.last_name,
      personal_email: data.company_representative.personal_email,
      cpf: data.company_representative.cpf.replace(/\D/g, ""),
      mobile_phone: data.company_representative.mobile_phone.replace(/\D/g, ""),
    },
    bank_account: {
      bank_name: data.bank_account.bank_name,
      bank_branch: data.bank_account.bank_branch,
      checking_account: data.bank_account.checking_account,
      checking_account_digit: data.bank_account.checking_account_digit,
    },
    signed_terms: {
      signed: data.signed_terms.signed,
      signed_at: data.signed_terms.signed_at || new Date().toISOString(),
      ip_address: data.signed_terms.ip_address || "127.0.0.1",
    },
  };

  if (data.document_type === "cnpj") {
    jsonData.cnpj = (data.cnpj ?? "").replace(/\D/g, "");
  } else {
    jsonData.cpf = (data.cpf ?? "").replace(/\D/g, "");
  }

  formData.append("data", JSON.stringify(jsonData));

  if (data.document.company_logo instanceof File) {
    formData.append("document.company_logo", data.document.company_logo);
  }
  if (data.document.cnpj_card instanceof File) {
    formData.append("document.cnpj_card", data.document.cnpj_card);
  }
  if (data.document.contract_signed?.length) {
    data.document.contract_signed.forEach((file) => {
      if (file instanceof File) {
        formData.append("document.contract_signed", file);
      }
    });
  }

  return formData;
}

/** Payload para PUT /organizations/:id/public/update na edição de sub-parceiro. */
export function buildSubPartnerUpdateFormData(
  data: TOrganizationRegisterFormData,
  openingDate?: string | Date | null,
): FormData {
  const formData = new FormData();

  const organizationData: Record<string, unknown> = {
    name: data.name,
    corporate_name: data.document_type === "cnpj" ? data.corporate_name : data.name,
    email: data.email,
    opening_date: toISO8601Date(openingDate),
    tax_classification: "simples_nacional",
    business_classification: data.business_classification,
    address: {
      postal_code: data.address.postal_code.replace(/\D/g, ""),
      street: data.address.street,
      number: data.address.number,
      complement: data.address.complement || "",
      neighborhood: data.address.neighborhood,
      city: data.address.city,
      state: data.address.state,
    },
    company_representative: {
      first_name: data.company_representative.first_name,
      last_name: data.company_representative.last_name,
      personal_email: data.company_representative.personal_email,
      cpf: data.company_representative.cpf.replace(/\D/g, ""),
      mobile_phone: data.company_representative.mobile_phone.replace(/\D/g, ""),
    },
    bank_account: {
      bank_name: data.bank_account.bank_name,
      bank_branch: data.bank_account.bank_branch,
      checking_account: data.bank_account.checking_account,
      checking_account_digit: data.bank_account.checking_account_digit,
    },
    signed_terms: {
      signed: data.signed_terms.signed,
      signed_at: data.signed_terms.signed_at || new Date().toISOString(),
      ip_address: data.signed_terms.ip_address || "127.0.0.1",
    },
  };

  if (data.document_type === "cnpj") {
    organizationData.cnpj = (data.cnpj ?? "").replace(/\D/g, "");
  } else {
    organizationData.cpf = (data.cpf ?? "").replace(/\D/g, "");
  }

  formData.append("data", JSON.stringify(organizationData));

  if (data.document.company_logo instanceof File) {
    formData.append("document.company_logo", data.document.company_logo);
  }
  if (data.document.cnpj_card instanceof File) {
    formData.append("document.cnpj_card", data.document.cnpj_card);
  }
  if (data.document.contract_signed?.length) {
    data.document.contract_signed.forEach((file) => {
      if (file instanceof File) {
        formData.append("document.contract_signed", file);
      }
    });
  }

  return formData;
}

export const useOrganizationRegister = (
  projectId: string,
  fatherOrganizationId: string | null,
  _level: number,
  onSuccess?: () => void
): IUseOrganizationRegister => {
  const { t } = useI18n();
  const { toast } = useToast();
  const queryClient = useQueryClient();

  const [foundOrganization, setFoundOrganization] = React.useState<any | null>(null);
  const [isSearching, setIsSearching] = React.useState(false);

  const form = useForm<TOrganizationRegisterFormData>({
    resolver: zodResolver(organizationRegisterFormSchema) as any,
    defaultValues: defaultOrganizationFormValues,
  });

  const handleCpfSearch = React.useCallback(
    async (cpf: string) => {
      const cleanCpf = cpf.replace(/\D/g, "");
      if (cleanCpf.length !== 11) {
        toast({
          title: t("generic.error"),
          description: "CPF inválido",
          variant: "destructive",
        });
        return;
      }

      setIsSearching(true);
      try {
        const result = await projectsApi.findOrganizationByCpf(cleanCpf);
        if (result) {
          setFoundOrganization(result);
          form.reset({
            document_type: result.cnpj ? "cnpj" : "cpf",
            name: result.name || "",
            cnpj: result.cnpj || "",
            cpf: result.cpf || "",
            corporate_name: result.corporate_name || "",
            email: result.email || "",
            business_classification: result.business_classification || "",
            address: result.address
              ? {
                  postal_code: result.address.postal_code || "",
                  street: result.address.street || "",
                  number: result.address.number || "",
                  complement: result.address.complement || "",
                  neighborhood: result.address.neighborhood || "",
                  city: result.address.city || "",
                  state: result.address.state || "",
                }
              : form.getValues("address"),
            company_representative: result.company_representative
              ? {
                  first_name: result.company_representative.first_name || "",
                  last_name: result.company_representative.last_name || "",
                  personal_email: result.company_representative.personal_email || "",
                  cpf: result.company_representative.cpf || "",
                  mobile_phone: result.company_representative.mobile_phone || "",
                }
              : form.getValues("company_representative"),
            bank_account: result.bank_account
              ? {
                  bank_name: result.bank_account.bank_name || "",
                  bank_branch: result.bank_account.bank_branch || "",
                  checking_account: result.bank_account.checking_account || "",
                  checking_account_digit: result.bank_account.checking_account_digit || "",
                }
              : form.getValues("bank_account"),
            document: form.getValues("document"),
            signed_terms: form.getValues("signed_terms"),
          });
          toast({
            title: t("generic.success"),
            description: "Organização encontrada! Verifique os dados e complete o cadastro.",
          });
        } else {
          setFoundOrganization(null);
          toast({
            title: "Organização não encontrada",
            description: "Você pode cadastrar uma nova organização.",
          });
        }
      } catch (error: any) {
        setFoundOrganization(null);
        if (error?.response?.status !== 404) {
          toast({
            title: t("generic.error"),
            description: error?.message || "Erro ao buscar organização",
            variant: "destructive",
          });
        }
      } finally {
        setIsSearching(false);
      }
    },
    [form, toast, t]
  );

  const createOrganizationMutation = useMutation({
    mutationFn: async (data: TOrganizationRegisterFormData) => {
      if (!fatherOrganizationId) {
        throw new Error("ID da organização pai é obrigatório");
      }
      const formData = buildOrganizationFormData(data);
      return projectsApi.createOrganizationForProject(
        projectId,
        fatherOrganizationId,
        formData
      );
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["project", projectId] });
      queryClient.invalidateQueries({ queryKey: ["projects"] });
      toast({
        title: t("generic.success"),
        description: "Organização cadastrada e associada ao projeto com sucesso!",
      });
      resetForm();
      if (onSuccess) {
        onSuccess();
      }
    },
    onError: (error: Error) => {
      toast({
        title: t("generic.error"),
        description: error.message || "Erro ao cadastrar organização",
        variant: "destructive",
      });
    },
  });

  const handleSubmit = React.useCallback(
    async (data: TOrganizationRegisterFormData) => {
      await createOrganizationMutation.mutateAsync(data);
    },
    [createOrganizationMutation]
  );

  const resetForm = React.useCallback(() => {
    form.reset(defaultOrganizationFormValues);
    setFoundOrganization(null);
  }, [form]);

  const handleAddressBlur = React.useCallback(
    async (event: React.FocusEvent<HTMLInputElement>) => {
      const zipcode = event.target.value;
      if (!zipcode) return;

      try {
        const addressData = await clientsApi.fetchZipCode(zipcode);
        if (addressData) {
          form.setValue("address.state", addressData.state_acronym);
          form.setValue("address.city", addressData.city_name);
          form.setValue("address.neighborhood", addressData.neighborhood_name);
          form.setValue("address.street", addressData.street_full);
        } else {
          form.setError("address.postal_code", {
            type: "custom",
            message: "CEP não encontrado",
          });
        }
      } catch (error) {
        console.error("Error fetching address:", error);
      }
    },
    [form]
  );

  return {
    form,
    isSearching,
    isSubmitting: createOrganizationMutation.isPending,
    foundOrganization,
    handleCpfSearch,
    handleSubmit,
    resetForm,
    handleAddressBlur,
  };
};
